import React, { useCallback, useState } from "react";
import { useFirebase } from "../contexts/FirebaseContext";
import { isMissionFilled } from "../constants/missions";

const notifyMissionsChanged = () => {
  window.dispatchEvent(new CustomEvent("arcade-missions-changed"));
};

const AdminOnlinePosts = () => {
  const { getAllMissions, updateMission } = useFirebase();
  const [open, setOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const loadOnlinePosts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const allMissions = await getAllMissions();
      setPosts(allMissions.filter((mission) => isMissionFilled(mission)));
    } catch (err) {
      console.error(err);
      setError("Could not load online posts. Check Firebase permissions.");
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [getAllMissions]);

  const handleOpen = async () => {
    setOpen(true);
    await loadOnlinePosts();
  };

  const handleClose = () => {
    setOpen(false);
    setError("");
  };

  const handleDelete = async (post) => {
    const confirmed = window.confirm("Are you sure?");
    if (!confirmed) return;

    setDeletingId(post.id);
    setError("");
    try {
      await updateMission(post.id, {
        title: "",
        text: "",
        imageUrl: "",
      });
      setPosts((current) => current.filter((item) => item.id !== post.id));
      notifyMissionsChanged();
    } catch (err) {
      console.error(err);
      setError("Could not delete post. Try again.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <button
        type="button"
        className="admin-button admin-button--ghost"
        onClick={handleOpen}
      >
        Online posts
      </button>

      {open && (
        <div className="admin-online-overlay" onClick={handleClose}>
          <div
            className="admin-online-panel"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-online-title"
          >
            <div className="admin-online-header">
              <div>
                <h2 id="admin-online-title">Online posts</h2>
                <p className="admin-online-sub">
                  Mission posts that are live on the site right now.
                </p>
              </div>
              <div className="admin-online-header-actions">
                <button
                  type="button"
                  className="admin-button admin-button--ghost"
                  onClick={loadOnlinePosts}
                  disabled={loading}
                >
                  {loading ? "Checking…" : "Refresh"}
                </button>
                <button
                  type="button"
                  className="admin-button admin-button--ghost"
                  onClick={handleClose}
                >
                  Close
                </button>
              </div>
            </div>

            {error && <p className="admin-error">{error}</p>}

            {loading && posts.length === 0 && (
              <p className="admin-muted">Checking online posts…</p>
            )}

            {!loading && posts.length === 0 && !error && (
              <p className="admin-muted">No posts are online right now.</p>
            )}

            {posts.length > 0 && (
              <ul className="admin-online-list">
                {posts.map((post) => (
                  <li key={post.id} className="admin-online-item">
                    <div className="admin-online-preview">
                      {post.imageUrl ? (
                        <img src={post.imageUrl} alt="" />
                      ) : (
                        <div className="admin-online-preview-empty">No pic</div>
                      )}
                    </div>
                    <div className="admin-online-body">
                      <div className="admin-online-slot">
                        Slot {String(post.slot).padStart(2, "0")}
                      </div>
                      <div className="admin-online-title">
                        {post.title?.trim() || "Untitled mission"}
                      </div>
                      {post.text?.trim() && (
                        <p className="admin-online-text">{post.text}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      className="admin-claim-delete"
                      disabled={deletingId === post.id}
                      onClick={() => handleDelete(post)}
                    >
                      {deletingId === post.id ? "Deleting…" : "Delete"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AdminOnlinePosts;
